import { useMutation, useQuery } from '@apollo/client';
import { Button, Card, message, Popconfirm, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DELETE_NIGHTLY_COUNT_MUTATION,
  RESEND_NIGHTLY_COUNT_MUTATION,
} from '../../graphql/mutations';
import { GET_NIGHTLY_COUNTS_LIST_QUERY } from '../../graphql/queries';
import { useNightlyCountAccess } from '../../hooks/useNightlyCountAccess';

const DEFAULT_PAGE_SIZE = 30;

interface INightlyCountRow {
  id: string;
  officeId: string;
  office?: { id: string; name: string; regionName?: string | null } | null;
  countDate: string;
  createdByUser?: { firstName: string; lastName: string } | null;
  lastEditedAt?: string | null;
  lastEditedByUser?: { firstName: string; lastName: string } | null;
  items: { id: string; productTitle: string; count: number }[];
}

const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const NightlyCountHistoryTable: FC = () => {
  const { t } = useTranslation('nightlyCount.NightlyCountHistoryTable');
  const access = useNightlyCountAccess();

  const pagination = usePagination({ pageSize: DEFAULT_PAGE_SIZE });

  const { data, loading, refetch } = useQuery<{
    getNightlyCountsList: { data: INightlyCountRow[]; meta: { total: number } };
  }>(GET_NIGHTLY_COUNTS_LIST_QUERY, {
    variables: {
      input: { take: DEFAULT_PAGE_SIZE, skip: pagination.skip },
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (data?.getNightlyCountsList?.meta?.total !== undefined) {
      pagination.setTotal(data.getNightlyCountsList.meta.total);
    }
  }, [data?.getNightlyCountsList?.meta?.total]);

  const [deleteNightlyCount] = useMutation(DELETE_NIGHTLY_COUNT_MUTATION);
  const [resendNightlyCount] = useMutation(RESEND_NIGHTLY_COUNT_MUTATION);

  const handleDelete = async (id: string) => {
    try {
      await deleteNightlyCount({ variables: { input: { id } } });
      message.success(t('deleteSuccess'));
      refetch();
    } catch (e) {
      message.error(`${t('deleteError')}: ${(e as Error).message}`);
    }
  };

  const handleResend = async (id: string) => {
    try {
      await resendNightlyCount({ variables: { input: { id } } });
      message.success(t('resendSuccess'));
    } catch (e) {
      message.error(`${t('resendError')}: ${(e as Error).message}`);
    }
  };

  const columns: ColumnsType<INightlyCountRow> = [
    {
      title: t('date'),
      dataIndex: 'countDate',
      key: 'countDate',
      render: (date: string, row) => (row.lastEditedAt ? `${date} *` : date),
    },
    {
      title: t('office'),
      key: 'office',
      render: (_, row) => row.office?.name || '',
    },
    {
      title: t('createdBy'),
      key: 'createdBy',
      render: (_, row) =>
        row.createdByUser
          ? `${row.createdByUser.firstName} ${row.createdByUser.lastName}`
          : '',
    },
    {
      title: t('edited'),
      key: 'edited',
      render: (_, row) =>
        row.lastEditedAt
          ? `${formatDateTime(row.lastEditedAt)}${
              row.lastEditedByUser
                ? ` — ${row.lastEditedByUser.firstName} ${row.lastEditedByUser.lastName}`
                : ''
            }`
          : '',
    },
    {
      title: t('items'),
      key: 'items',
      render: (_, row) => t('countCount', { count: row.items?.length || 0 }),
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, row) => {
        const canEdit = access.canEditOffice(row.officeId);
        const canResend = canEdit;
        return (
          <Space>
            {access.canDelete && (
              <Popconfirm
                title={t('deleteConfirm')}
                onConfirm={() => handleDelete(row.id)}
              >
                <Button danger size="small">
                  {t('delete')}
                </Button>
              </Popconfirm>
            )}
            {canResend && (
              <Button size="small" onClick={() => handleResend(row.id)}>
                {t('resend')}
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card>
      <Table<INightlyCountRow>
        rowKey={(r) => r.id}
        loading={loading}
        columns={columns}
        dataSource={data?.getNightlyCountsList?.data || []}
        pagination={pagination.config}
        scroll={{ x: 800 }}
      />
    </Card>
  );
};

export default NightlyCountHistoryTable;
